import React, { useState, useEffect } from "react";
import CognexLogo from "../../general/CognexLogo";
import Extensions from "../extensions/Extensions";
import ExtensionCreation from "../extensions/ExtensionCreation/ExtensionCreation";
import Loader from "../../general/Loader";
import "./Banner.css";
import MUIButton from "@mui/material/Button";

import { Button } from "primereact/button";
import AddIcon from "@mui/icons-material/Add";

import getExtensionsByUserId from "../../../utils/ExtensionHandlingUtils/getExtensionsByUserId";
import getPublicExtensions from "../../../utils/ExtensionHandlingUtils/getPublicExtensions";
import useUserDetails from "../../../utils/auth/UserDetails";
import { Dialog } from "primereact/dialog";
import { Divider } from "@mui/material";

const Banner = ({ onStartNewChat, onSectionChange, setSelectedSection, setNav, selectedFilters }) => {

    const userDetails = useUserDetails();

    const [userExtensions, setUserExtensions] = useState([]);
    const [featuredExtensions, setFeaturedExtensions] = useState([]);
    const [isLoadingUserExtensions, setIsLoadingUserExtensions] = useState(true);
    const [isLoadingFeatured, setIsLoadingFeatured] = useState(true);
    const [isCreatingExtension, setIsCreatingExtension] = useState(false);
    const [triggerCreate, setTriggerCreate] = useState(false);
    const [triggerCancel, setTriggerCancel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchUserExtensions = async () => {
            if (!userDetails?.userId) {
                setIsLoadingUserExtensions(false);
                return;
            }
            try {
                setIsLoadingUserExtensions(true);
                const fetched = await getExtensionsByUserId(userDetails.userId);
                setUserExtensions(fetched);
            } catch (error) {
                console.error("Error loading user extensions:", error);
            } finally {
                setIsLoadingUserExtensions(false);
            }
        };
        fetchUserExtensions();
    }, [userDetails]);

    useEffect(() => {
        const fetchFeatured = async () => {
            if (!userDetails?.userId) {
                return;
            }
            try {
                setIsLoadingFeatured(true);
                const publicExts = await getPublicExtensions(userDetails.userId, userDetails.username);
                setFeaturedExtensions(publicExts);
            } catch (error) {
                console.error("Error loading public extensions:", error);
            } finally {
                setIsLoadingFeatured(false);
            }
        };
        fetchFeatured();
    }, [userDetails]);

    const filteredUserExtensions = userExtensions.filter(ext => {
        const selectedCategory = selectedFilters.category;

        const matchesCategory =
            !selectedCategory ||  // No category filter applied
            (typeof selectedCategory === 'object' && selectedCategory.label === "All Categories") || // "All Categories" case
            (typeof selectedCategory === 'string' && ext.category === selectedCategory); // Matching category case

        const matchesSearch = selectedFilters.search
            ? ext.extensionName?.toLowerCase().includes(selectedFilters.search.toLowerCase())
            : true;

        return matchesCategory && matchesSearch;
    });

    const filteredFeaturedExtensions = featuredExtensions.filter(ext => {
        const selectedCategory = selectedFilters.category;

        const matchesCategory =
            !selectedCategory ||
            (typeof selectedCategory === 'object' && selectedCategory.label === "All Categories") ||
            (typeof selectedCategory === 'string' && ext.category === selectedCategory);

        const matchesSearch = selectedFilters.search
            ? ext.extensionName?.toLowerCase().includes(selectedFilters.search.toLowerCase())
            : true;

        return matchesCategory && matchesSearch;
    });


    const handleCreateExtensionClick = () => {
        setIsCreatingExtension(true);
    };

    const handleExtensionCreated = (newExtension) => {
        setUserExtensions((prev) => [...prev, newExtension]);
        setIsCreatingExtension(false);
    };

    const handleCancelCreation = () => {
        setIsCreatingExtension(false);
    };

    const handleUpdateExtension = (updatedExtension) => {
        setUserExtensions((prev) =>
            prev.map((ext) => (ext.id === updatedExtension.id ? updatedExtension : ext))
        );

        if (updatedExtension.publicationType === "public") {
            setFeaturedExtensions((prev) => {
                const exists = prev.some((ext) => ext.id === updatedExtension.id);
                if (!exists) return [...prev, updatedExtension];
                return prev.map((ext) => (ext.id === updatedExtension.id ? updatedExtension : ext));
            });
        } else {
            setFeaturedExtensions((prev) =>
                prev.filter((ext) => ext.id !== updatedExtension.id)
            );
        }
    };

    const handleRemoveExtension = (deletedExtensionId) => {
        setUserExtensions((prev) => prev.filter((ext) => ext.id !== deletedExtensionId));
        setFeaturedExtensions((prev) => prev.filter((ext) => ext.id !== deletedExtensionId));
    };



    return (
        <div className="banner-container">
            {
                isLoadingUserExtensions || isLoadingFeatured ? (
                    <Loader />
                ) : (

                    <div className="banner-card">
                        <Dialog
                            visible={isCreatingExtension}
                            onHide={handleCancelCreation}
                            header="Create Extension"
                            modal
                            draggable={false}
                            style={{ width: "50vw" }}
                            footer={
                                <div className="dialog-button">
                                    <Button
                                        label="Cancel"
                                        icon="pi pi-times"
                                        className="p-button-text p-button-customtext"
                                        onClick={() => setTriggerCancel(true)}  // Trigger cancel event
                                    />
                                    <Button
                                        label={isLoading ? "Creating..." : "Create"}
                                        icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                        className="p-button-primary"
                                        onClick={() => setTriggerCreate(true)}
                                        disabled={isLoading} // Disable button when loading
                                    />

                                </div>
                            }
                        >
                            <ExtensionCreation
                                onExtensionCreated={handleExtensionCreated}
                                onCancel={handleCancelCreation}
                                triggerCreate={triggerCreate}
                                triggerCancel={triggerCancel}
                                resetTriggers={() => {
                                    setTriggerCreate(false);
                                    setTriggerCancel(false);
                                }}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                            />
                        </Dialog>


                        <div className="banner-header">
                            <div></div>
                            <div className="header-container">
                                <h2>
                                    Welcome to{" "}
                                    <strong>DoAssist</strong>
                                </h2>
                                <p>Secure and personalized Cognitive-AI companion</p>
                            </div>

                            <MUIButton
                                variant="contained"
                                color="success"
                                sx={{ width: 160, height: 40 }}
                                className="create-extension-button"
                                onClick={handleCreateExtensionClick}
                            >
                                Create
                            </MUIButton>
                        </div>
                        <div className="extension-content">
                            <div className="your-extensions">
                                <div className="your-extensions-header">
                                    <h3>My Extensions</h3>

                                </div>
                                {userExtensions.length === 0 ? (
                                    <p className="no-user-extensions">
                                        <i className="pi pi-info-circle" style={{ marginRight: "8px" }} />
                                        You have not created any extensions yet.
                                    </p>
                                ) : (
                                    <Extensions
                                        extensions={filteredUserExtensions}
                                        onStartNewChat={onStartNewChat}
                                        onUpdateExtension={handleUpdateExtension}
                                        onRemoveExtension={handleRemoveExtension}
                                        onSectionChange={onSectionChange}
                                        setSelectedSection={setSelectedSection}
                                        setNav={setNav}
                                        featured={false}
                                    
                                    />
                                )}
                            </div>
                            <Divider orientation="horizontal" sx={{ marginY: 6 }} />
                            <div className="banner-actions">
                                <h3>Featured Extensions</h3>
                            </div>
                            <div className="banner-content">
                                {featuredExtensions.length === 0 ? (
                                    <p className="no-featured-extensions">
                                        <i className="pi pi-info-circle" style={{ marginRight: "8px" }} />
                                        No public extensions found.
                                    </p>
                                ) : (
                                    <Extensions
                                        extensions={filteredFeaturedExtensions}
                                        onStartNewChat={onStartNewChat}
                                        onUpdateExtension={handleUpdateExtension}
                                        onRemoveExtension={handleRemoveExtension}
                                        onSectionChange={onSectionChange}
                                        setSelectedSection={setSelectedSection}
                                        setNav={setNav}
                                        featured={true}

                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default Banner;